/**
 * /data-sim-cards — SEO landing page
 * Target: "data sim cards", "data only sim", "data sim card UK", "SIM card for router/MiFi"
 * Gap identified: Competitors (Lebara, iD, Giffgaff) have NO dedicated data-only SIM page.
 * Three & Vodafone have them but miss "data SIM for router" angle.
 */
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Wifi, Tablet, Router, Briefcase, Signal, Globe } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import SiluxPromise from '@/components/SiluxPromise'

const FAQS = [
  {
    question: 'What is a data SIM card?',
    answer: 'A data SIM card is a SIM that provides mobile data (internet access) without calls or texts. They\'re perfect for tablets, MiFi routers, laptops, IoT devices, GPS trackers and mobile broadband. Silux Mobile data SIMs are available on Three, Vodafone, EE and O2.',
  },
  {
    question: 'Can I use a data SIM in a router or MiFi device?',
    answer: 'Yes! Our data-only SIM cards work in all MiFi devices, 4G/5G routers, portable WiFi hotspots and USB dongles. Simply insert the SIM and share the connection with multiple devices.',
  },
  {
    question: 'What is the difference between a data SIM and a regular SIM?',
    answer: 'A regular SIM includes calls, texts and data. A data SIM is data-only — no calls or texts. They\'re cheaper for devices that only need internet access, like tablets, routers and IoT devices.',
  },
  {
    question: 'Do data SIM cards work for home broadband?',
    answer: 'Yes — many customers use our data SIM cards as mobile broadband for home internet. Pop the SIM into a 4G/5G router and enjoy fast home broadband without a fixed landline.',
  },
  {
    question: 'Can I use a data SIM in any country?',
    answer: 'Yes — our data SIM cards work abroad (Three SIMs use Go Roam in 71+ countries). Roaming is not free: roaming charges vary from package to package, so check the roaming charges for your plan before you travel.',
  },
  {
    question: 'Is there a minimum contract on data SIM cards?',
    answer: 'It depends on the plan you choose. Our data SIMs are available on a 30-day rolling plan with no minimum term, or on a 3, 6, 12 or 24-month contract at a lower monthly price. A contract has a minimum term for its length.',
  },
  {
    question: 'Will my price increase during my contract?',
    answer: 'No. Silux Mobile never raises your price mid-contract — there are no price increases during your contract and no annual or inflation-linked price rises. The price you sign up at is the price you pay.',
  },
  {
    question: 'Are plan and SIM upgrades free?',
    answer: 'Yes. Upgrades are free — move to a bigger data plan or a better SIM whenever you like at no extra cost, with no upgrade or admin fees.',
  },
  {
    question: 'Do you run a credit check?',
    answer: 'No. There are no credit checks on our SIM contracts, so you can join Silux Mobile regardless of your credit history. No credit check is required to order or activate your SIM.',
  },
  {
    question: 'Is Silux Mobile a good mobile provider?',
    answer: 'Yes. Silux Mobile is a UK SIM-only and mobile network provider with SIMs on Three, Vodafone, EE and O2, with cheap SIM deals, no price rises, free upgrades and no credit checks on SIM contracts.',
  },
]

const USE_CASES = [
  { icon: Tablet, title: 'Tablet & iPad SIM', desc: 'Keep your tablet connected with a dedicated data SIM. No WiFi hotspot needed.' },
  { icon: Router, title: 'MiFi & Router SIM', desc: 'Insert into any 4G/5G router to create your own WiFi hotspot at home or on the go.' },
  { icon: Wifi, title: 'Home Broadband', desc: 'Replace your landline broadband with a data SIM in a 4G/5G router.' },
  { icon: Briefcase, title: 'Business & IoT', desc: 'Connect IoT devices, GPS trackers, CCTV cameras and remote sensors.' },
  { icon: Signal, title: 'Backup Internet', desc: 'Keep a data SIM as a backup internet connection for outages.' },
  { icon: Globe, title: 'Travel SIM', desc: 'Use your data SIM in 71+ countries with Three Go Roam. Roaming is an extra cost and varies from package to package.' },
]

export default function DataSimCards() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Data SIM Cards', url: 'https://www.siluxmobile.co.uk/data-sim-cards' },
  ]

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Data SIM Cards UK',
    description: 'Mobile data-only SIM cards for tablets, MiFi routers, IoT and home broadband. Three network, 5G included.',
    provider: { '@type': 'Organization', name: 'Silux Mobile', url: 'https://www.siluxmobile.co.uk' },
    serviceType: 'Data SIM Card',
    areaServed: 'United Kingdom',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Data SIM Plans',
      itemListElement: [
        { '@type': 'Offer', name: 'Data Only SIM — from £5/month', price: '5', priceCurrency: 'GBP' },
      ],
    },
  }

  return (
    <>
      <SEOHead
        title="Data SIM Cards UK | Tablet, Router & MiFi | Silux Mobile"
        description="Buy data SIM cards from Silux Mobile — data-only SIMs for tablets, MiFi routers, home broadband, IoT & travel. Three 5G network. From £5/month. No contract. Free delivery. No price rises, free upgrades and no credit checks on SIM contracts."
        keywords={[
          'data sim cards', 'data only sim', 'data SIM card UK', 'SIM card for router',
          'data sim for MiFi', 'tablet sim card', 'data sim for tablet', 'IoT sim card',
          'mobile data sim UK', 'broadband sim card', 'Three data sim', '5G data sim card',
          'no contract data sim', 'data sim for home broadband', 'mobile broadband SIM',
          'data SIM provider', 'SIM only provider', 'best SIM only provider UK',
          'mobile network provider', 'cheap mobile provider', 'pay monthly SIM provider',
        ]}
        canonical="https://www.siluxmobile.co.uk/data-sim-cards"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('/grid.svg')]" />
        <div className="max-w-5xl mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <Signal className="w-4 h-4" />
            Data SIM Cards — Three 5G Network
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Data SIM Cards UK
            <span className="block text-2xl md:text-3xl font-normal text-white/80 mt-2">
              Data-only SIM for tablet, router, MiFi & IoT
            </span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Silux Mobile data SIM cards are perfect for any device that needs internet without calls.
            Tablets, MiFi routers, home broadband, IoT devices — all running on Three's 5G network.
            From just £5/month — 30-day rolling or 3 to 24-month contracts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/sim-only/data-only"
              className="px-8 py-4 bg-white text-blue-700 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Browse Data SIM Plans
            </Link>
            <Link
              to="/sim-only"
              className="px-8 py-4 bg-white/20 border-2 border-white/40 text-white rounded-2xl font-bold hover:bg-white/30 transition-all"
            >
              Get Free SIM Card
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-white/70">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> No Calls or Texts — Pure Data</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 5G Included Free</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 71+ Countries Go Roam</span>
          </div>
        </div>
      </section>

      <SiluxPromise variant="sim" />

      {/* What is a data SIM — feeds AI Overviews */}
      <section className="py-12 px-4 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What is a Data SIM Card?</h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            A <strong>data SIM card</strong> is a SIM that provides mobile internet access without calls or texts.
            You insert it into a tablet, MiFi device, 4G/5G router, dongle or any data-capable device.
            Data SIM cards from Silux Mobile run on <strong>Three's award-winning 5G network</strong>,
            giving you fast, reliable internet wherever you are in the UK. As a UK data SIM provider and SIM only provider,
            Silux Mobile keeps things simple — no price rises and no credit checks.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Who Uses Data SIM Cards?
          </h2>
          <p className="text-center text-gray-600 mb-10">
            Data SIM cards are incredibly versatile. Here are the most popular uses:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {USE_CASES.map(uc => (
              <div key={uc.title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all">
                <uc.icon className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">{uc.title}</h3>
                <p className="text-sm text-gray-600">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Data SIM Card Plans</h2>
          <p className="text-gray-600 mb-8">All plans on Three's 5G network. 30-day rolling or 3 to 24-month contracts. Free delivery.</p>
          <Link
            to="/sim-only/data-only"
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            View All Data SIM Plans
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Competitor comparison callout */}
      <section className="py-12 px-4 bg-blue-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            Why Silux Mobile Data SIMs Beat the Rest
          </h2>
          <p className="text-gray-600 mb-6">
            Unlike Lebara, Giffgaff and iD Mobile who have no dedicated data SIM offering —
            Silux Mobile specialises in data-only SIMs. We also include <strong>Three Go Roam</strong> in 71+ countries,
            which SMARTY and Vodafone's budget plans don't offer.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['5G Included', '✅ Free'],
              ['Go Roam', '71+ countries'],
              ['Plan length', '30-day rolling or 3–24 month contract'],
              ['Free Delivery', 'Royal Mail Tracked 48'],
            ].map(([label, value]) => (
              <div key={label} className="bg-white rounded-xl p-4 border border-blue-100">
                <p className="font-bold text-blue-600 text-sm">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Data SIM Card FAQs</h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <details key={faq.question} className="group border border-gray-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900 hover:bg-gray-50">
                  {faq.question}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">Explore More</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Cheap SIM Cards', to: '/cheap-sim-cards' },
              { label: 'Mobile Broadband', to: '/mobile-broadband' },
              { label: 'Cheap Broadband', to: '/cheap-broadband' },
              { label: 'Multi-SIM Plans', to: '/sim-only/multisim' },
              { label: 'Long-Life SIM', to: '/sim-only/longlife-sim' },
              { label: 'Business SIMs', to: '/sim-only/guarddata-sim' },
              { label: 'Roaming SIMs', to: '/roaming' },
              { label: 'Network Coverage', to: '/network-coverage' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="block p-3 bg-white rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-blue-300 hover:text-blue-700 transition-all text-center"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
